<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="save"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab v-for="contentType in contentTypeList" :key="contentType.id">
        {{ contentType.name }}
      </v-tab>
      <v-tab-item
        v-for="(contentType, ctIndex) in contentTypeList"
        :key="contentType.id"
      >
        <v-form class="mx-10">
          <v-row justify="center" align="center" v-show="contentType.loading">
            <div class="text-center">
              <div>
                <v-progress-circular
                  class="my-4"
                  :size="50"
                  width="5"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>
              <p class="display-1">{{ $t("Loading") }}...</p>
            </div>
          </v-row>
          <v-row v-if="!contentType.loading">
            <v-col
              class="pa-0 ma-0"
              md="12"
              v-for="(field, fieldIndex) in contentType.document.fields"
              v-show="field.isVisible && !field.isReadOnly"
              v-bind:key="`document-modal-field-${field.defFieldId}`"
            >
              <v-row class="pa-0 ma-0">
                <v-col class="pa-0 ma-0" md="3">
                  <v-switch
                    v-model="field.changed"
                    v-bind:label="$t('Edit')"
                    color="primary"
                    hide-details
                  ></v-switch>
                </v-col>
                <v-col class="pa-0 ma-0" md="9">
                  <DocumentField
                    :ref="field.code"
                    :field="field"
                    :documentId="contentType.document.id"
                    @on-field-change="onFieldChange"
                    :disabled="!field.changed"
                    :validator.sync="$v"
                    :index="fieldIndex"
                    :ctIndex="ctIndex"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </v-tab-item>
    </v-tabs>
  </base-modal>
</template>
<script>
import { requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import DocumentService from "@/services/DocumentService";
import Document from "@/ecm/models/document/Document";
import DocumentField from "@/components/document/documentModal/field/DocumentField";
import Utils from "@/ecm/common/Utils";
import { EventBus } from "@/eventBus.js";

export default {
  components: {
    DocumentField,
  },
  created() {
    this.$v.$reset();
  },
  data() {
    return {
      modal: {
        title: this.$t("EditDocuments"),
        key: "EditMultipleModal",
      },
      documentList: {},
      document: {},
      documentIds: [],
      contentTypeList: [],
      open: false,
      loading: false,
      saving: false,
      selectedTab: 0,
    };
  },
  mixins: [validationMixin],
  validations: {
    contentTypeList: {
      $each: {
        document: {
          fields: {
            $each: {
              value: {
                required: requiredIf(function (field) {
                  return field.isRequired && field.changed;
                }),
              },
            },
          },
        },
      },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
  },
  methods: {
    onFieldChange(field) {
      if (field.fieldsThatDependOn.length > 0)
        this.$nextTick(() => {
          this.refreshList(field.fieldsThatDependOn);
        });
    },
    refreshList(fieldsToRefresh) {
      fieldsToRefresh.forEach((fieldCode) => {
        const fieldToRefresh = this.contentTypeList
          .flatMap((ct) => ct.document.fields)
          .filter((f) => f.code == fieldCode)[0];

        const parameters = [];

        fieldToRefresh.fieldsOnWhichDepends.forEach((paramCode) => {
          var paramValue = this.contentTypeList
            .flatMap((ct) => ct.document.fields)
            .filter((f) => f.code == paramCode)[0].value;

          parameters.push({
            Name: paramCode,
            Value: paramValue || "",
          });
        });

        EventBus.$emit("refreshListElements" + fieldToRefresh.code, parameters);
      });
    },
    loadContentTypes() {
      for (let i = 0; i < this.contentTypeList.length; i++) {
        this.contentTypeList[i].loading = true;

        DocumentService.getStructure(this.contentTypeList[i].id).then((dto) => {
          this.contentTypeList[i].document = new Document(dto);
          this.contentTypeList[i].loading = false;
        });
      }
    },
    async save() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        //SELECT TAB
        for (let i = 0; i < this.contentTypeList.length; i++) {
          if (this.$v.contentTypeList.$each[i].$invalid) {
            this.selectedTab = i;
            break;
          }
        }
        return;
      }

      var documents = [];
      var uniqueFields = [];
      this.documentList.forEach((documentItem) => {
        let contentType = this.contentTypeList.filter(
          (c) => c.id == documentItem.contentTypeId
        )[0];

        var fields = contentType.document.fields.filter((f) => f.changed);

        if (fields.length > 0) {
          var document = Utils.object.toJS(contentType.document);
          document = Object.assign(new Document(), document);
          document.id = documentItem.id;
          document.fields = Utils.object.toJS(fields);
          documents.push(document.toDto([]));
          uniqueFields.push(...document.fields.filter((f) => f.isUnique && f.value != "").map((f) => f.title));
        }
      });

      if (documents.length == 0) {
        Utils.toast.error(this.$t("FIeldsUpdateRequired"));
        return;
      }

      if (uniqueFields.length > 0) {
        var confirmMessage = this.$t(
          "UniqueFieldsPossibleDuplicatesWarning"
        ).replaceAll("{0}", [...new Set(uniqueFields)].join());

        var res = await this.$confirm(confirmMessage, {
          title: this.$t("Confirm"),
          icon: "mdi-alert",
          color: "warning",
          buttonTrueText: this.$t("Save"),
          buttonFalseText: this.$t("Cancel"),
        });

        if (res != 1) {
          return;
        }
      }

      this.modalInstance.progress(true);

      DocumentService.updateMultiple(documents)
        .then((result) => {
          this.hide();
          Utils.toast.success(this.$t("DocumentsUpdatedSuccessfully"));
          EventBus.$emit("refreshDocumentList");
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    showModal(documentList, contentTypeList) {
      this.documentList = documentList;
      this.document = documentList[0];
      this.contentTypeList = contentTypeList;
      this.selectedTab = 0;
      this.loadContentTypes();
      this.show();
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
  },
};
</script>